import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useShop } from '../context/ShopContext';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

export default function CartDrawer() {
  const { cart, isCartOpen, setIsCartOpen, cartTotal, removeFromCart, updateQuantity } = useShop();

  return (
    <AnimatePresence>
      {isCartOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsCartOpen(false)}
            className="fixed inset-0 bg-graphite/40 backdrop-blur-sm z-[60]"
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-bone z-[70] flex flex-col shadow-2xl"
          >
            <div className="p-6 border-b border-graphite/10 flex justify-between items-center">
              <h2 className="text-xl font-bold tracking-tighter uppercase">Your Bag</h2>
              <button 
                onClick={() => setIsCartOpen(false)}
                className="p-2 hover:text-crimson transition-colors"
                aria-label="Close cart"
              >
                <SafeIcon icon={FiIcons.FiX} className="text-xl" />
              </button>
            </div>

            <div className="flex-grow overflow-y-auto p-6 no-scrollbar">
              {cart.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <p className="text-graphite/40 uppercase tracking-widest text-xs mb-4">The bag is currently empty.</p>
                  <button 
                    onClick={() => setIsCartOpen(false)}
                    className="border-b border-graphite pb-1 text-sm font-bold uppercase tracking-widest hover:text-crimson hover:border-crimson transition-colors"
                  >
                    Continue Browsing
                  </button>
                </div>
              ) : (
                <div className="space-y-8">
                  {cart.map((item) => (
                    <div key={`${item.id}-${item.selectedSize}`} className="flex gap-4">
                      <div className="w-24 aspect-[3/4] bg-gray-100 overflow-hidden">
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-grow flex flex-col">
                        <div className="flex justify-between items-start mb-1">
                          <h3 className="text-sm font-bold uppercase tracking-tight">{item.name}</h3>
                          <button 
                            onClick={() => removeFromCart(item.id, item.selectedSize)}
                            className="text-graphite/40 hover:text-crimson transition-colors"
                          >
                            <SafeIcon icon={FiIcons.FiTrash2} className="text-sm" />
                          </button>
                        </div>
                        <p className="text-xs text-graphite/60 uppercase tracking-widest mb-4">Size: {item.selectedSize}</p>
                        <div className="mt-auto flex justify-between items-center">
                          <div className="flex items-center border border-graphite/10">
                            <button 
                              onClick={() => updateQuantity(item.id, item.selectedSize, -1)}
                              className="px-3 py-1 hover:bg-graphite hover:text-bone transition-colors"
                            >-</button>
                            <span className="px-3 text-xs font-bold">{item.quantity}</span>
                            <button 
                              onClick={() => updateQuantity(item.id, item.selectedSize, 1)}
                              className="px-3 py-1 hover:bg-graphite hover:text-bone transition-colors"
                            >+</button>
                          </div>
                          <span className="text-sm font-bold">${item.price * item.quantity}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {cart.length > 0 && (
              <div className="p-6 border-t border-graphite/10 bg-white/50">
                <div className="flex justify-between items-center mb-6">
                  <span className="text-xs uppercase tracking-widest text-graphite/60">Subtotal</span>
                  <span className="text-xl font-bold tracking-tighter">${cartTotal}</span>
                </div>
                <Link 
                  to="/checkout"
                  onClick={() => setIsCartOpen(false)}
                  className="block w-full bg-graphite text-bone text-center py-4 text-xs uppercase tracking-widest font-bold hover:bg-crimson transition-colors duration-300"
                >
                  Proceed to Checkout
                </Link>
                <p className="text-[10px] text-center text-graphite/40 mt-4 uppercase tracking-widest">
                  Shipping and taxes calculated at next step
                </p>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}